import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import * as THREE from "three";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import {
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiLoader,
  FiLock,
  FiMaximize,
  FiMinimize,
  FiMove,
  FiRefreshCw,
  FiTrash2,
  FiUpload,
  FiX,
  FiZoomOut,
} from "react-icons/fi";
import OcclusionViewer, {
  IDENTITY_JAW,
  jawTransformToMatrix,
  type JawTransform,
  type OcclusionViewerHandle,
  type OcclusionViewerMeshes,
} from "../../components/occlusion/OcclusionViewer";
import { buildBvh, registerMeshToTarget } from "../../components/occlusion/icpRegister";
import { computeBiteFrame, type BiteFrame } from "../../components/occlusion/occlusalFrame";
import {
  checkScanUnits,
  splitCombinedJaws,
  weldGeometry,
} from "../../components/occlusion/meshPrep";
import { matrixToRowMajor } from "../../components/occlusion/linalg";
import {
  ARCH_SECTORS,
  CONTACT_MM,
  LIGHT_CONTACT_MM,
  NEAR_CONTACT_MM,
  PENETRATION_TOLERANCE_MM,
  analyzeOcclusalContacts,
  estimateBiteQualityFromStats,
  findBiteOptions,
  findContactAdjustments,
  jawTransformKey,
  MAX_CONTACT_ADJUSTMENTS,
  resolveArchKind,
  type ArchScanKind,
  type ArchScanProfile,
  type BiteOption,
  type BiteOptimizeProgress,
  type BiteOptimizeQuality,
  type ContactAdjustment,
  type ContactStats,
  type ExclusionRegion,
  type PriorityRegion,
  type SegmentSide,
} from "../../components/occlusion/contactAnalysis";
import { applyMatrixToGeometryLite, deltaJawTransform } from "../../components/occlusion/meshMath";

type Slot = "upper" | "lower" | "bite" | "combined";
type InputMode = "separate" | "combined";
type ActiveJaw = "upper" | "lower";
type LockedJaw = "none" | "upper" | "lower";
type BiteViewMode = "optimized" | "original" | "compare";
type Stage =
  | "idle"
  | "loading"
  | "registering"
  | "analyzing"
  | "done"
  | "error";

interface PoseSnapshot {
  upper: JawTransform;
  lower: JawTransform;
}

interface OriginalBiteSnapshot {
  upper: THREE.BufferGeometry;
  lower: THREE.BufferGeometry;
  stats: ContactStats;
}

/** Brush radii for priority / exclusion paint (mm). */
const BRUSH_SIZE_OPTIONS_MM = [0.5, 0.75, 1, 1.5, 2, 2.5, 3, 5, 8] as const;

const triggerDownload = (filename: string, blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const geometryToBinaryStlBlob = (geometry: THREE.BufferGeometry): Blob => {
  const mesh = new THREE.Mesh(geometry);
  const data = new STLExporter().parse(mesh, { binary: true });
  if (data instanceof DataView) {
    const bytes = new Uint8Array(data.byteLength);
    bytes.set(new Uint8Array(data.buffer as ArrayBuffer, data.byteOffset, data.byteLength));
    return new Blob([bytes.buffer as ArrayBuffer], { type: "model/stl" });
  }
  return new Blob([data as string], { type: "model/stl" });
};

interface BiteRegistrationInfo {
  upperRms: number;
  lowerRms: number;
  /** Share of each arch's surface lying on the bite record (0–1). */
  upperOverlap: number;
  lowerOverlap: number;
  upperStart: string;
  lowerStart: string;
  skipped: boolean;
}

/** Below this overlap the arch probably did not lock onto the bite record. */
const LOW_OVERLAP = 0.06;

interface SlotState {
  file: File | null;
  name: string;
}

const emptySlots: Record<Slot, SlotState> = {
  upper: { file: null, name: "" },
  lower: { file: null, name: "" },
  bite: { file: null, name: "" },
  combined: { file: null, name: "" },
};

const loadRawStl = async (file: File): Promise<THREE.BufferGeometry> =>
  new STLLoader().parse(await file.arrayBuffer());

const loadGeometry = async (file: File): Promise<THREE.BufferGeometry> => {
  const raw = await loadRawStl(file);
  const geometry = weldGeometry(raw);
  raw.dispose();
  return geometry;
};

const formatMm = (n: number) => `${n.toFixed(3)} mm`;

const TELESCOPIC_AXES = [
  { key: "tx" as const, label: "Lateral X" },
  { key: "ty" as const, label: "A-P Y" },
  { key: "tz" as const, label: "Close Z" },
];

const TILT_AXES = [
  { key: "rx" as const, label: "Pitch" },
  { key: "ry" as const, label: "Roll" },
  { key: "rz" as const, label: "Yaw" },
];

const PRECISION_MM = 0.02;
const PRECISION_DEG = 0.1;

const isIdentity = (t: JawTransform) =>
  t.tx === 0 &&
  t.ty === 0 &&
  t.tz === 0 &&
  t.rx === 0 &&
  t.ry === 0 &&
  t.rz === 0;

const OcclusionContactAnalysis = () => {
  const shellRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<OcclusionViewerHandle>(null);
  const baseUpperRef = useRef<THREE.BufferGeometry | null>(null);
  const baseLowerRef = useRef<THREE.BufferGeometry | null>(null);
  const baseLowerBvhRef = useRef<ReturnType<typeof buildBvh> | null>(null);
  const baseUpperBvhRef = useRef<ReturnType<typeof buildBvh> | null>(null);
  const biteRef = useRef<THREE.BufferGeometry | null>(null);
  /** Bite frame the working geometry lives in; exports map back to the scanner frame. */
  const frameRef = useRef<BiteFrame | null>(null);
  /** Original file → registered (bite record) frame, per arch. */
  const registrationRef = useRef<{ upper: THREE.Matrix4; lower: THREE.Matrix4 }>({
    upper: new THREE.Matrix4(),
    lower: new THREE.Matrix4(),
  });
  const recomputeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recomputeToken = useRef(0);
  const optimizeToken = useRef(0);
  const exclusionIdRef = useRef(1);
  const lastExclusionPaintRef = useRef(0);
  const priorityIdRef = useRef(1);
  const lastPriorityPaintRef = useRef(0);

  const [slots, setSlots] = useState(emptySlots);
  const [skipBite, setSkipBite] = useState(false);
  const [inputMode, setInputMode] = useState<InputMode>("separate");
  /** Combined STL: treat the half reported as lower as the upper arch instead. */
  const [swapCombined, setSwapCombined] = useState(false);
  const [biteOptions, setBiteOptions] = useState<BiteOption[]>([]);
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [showBite, setShowBite] = useState(false);
  const [showAllAdjustments, setShowAllAdjustments] = useState(true);
  const [panelOpen, setPanelOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [stage, setStage] = useState<Stage>("idle");
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [stats, setStats] = useState<ContactStats | null>(null);
  const [contactAdjustments, setContactAdjustments] = useState<ContactAdjustment[]>([]);
  const [selectedAdjustmentId, setSelectedAdjustmentId] = useState<number | null>(null);
  const [activeJaw, setActiveJaw] = useState<ActiveJaw>("upper");
  const [lockedJaw, setLockedJaw] = useState<LockedJaw>("none");
  const [excludePeaksEnabled, setExcludePeaksEnabled] = useState(false);
  const [excludePaintMode, setExcludePaintMode] = useState(false);
  const [exclusionRegions, setExclusionRegions] = useState<ExclusionRegion[]>([]);
  const [excludeBrushMm, setExcludeBrushMm] = useState(1);
  const [priorityEnabled, setPriorityEnabled] = useState(true);
  const [priorityPaintMode, setPriorityPaintMode] = useState(false);
  const [priorityRegions, setPriorityRegions] = useState<PriorityRegion[]>([]);
  const [priorityBrushMm, setPriorityBrushMm] = useState(1);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [upperTx, setUpperTx] = useState<JawTransform>(IDENTITY_JAW);
  const [lowerTx, setLowerTx] = useState<JawTransform>(IDENTITY_JAW);
  const [bakedUpperTx, setBakedUpperTx] = useState<JawTransform>(IDENTITY_JAW);
  const [bakedLowerTx, setBakedLowerTx] = useState<JawTransform>(IDENTITY_JAW);
  const [recomputing, setRecomputing] = useState(false);
  const [nudgeStep, setNudgeStep] = useState(PRECISION_MM);
  const [rotStep, setRotStep] = useState(PRECISION_DEG);
  const [alignmentDockVisible, setAlignmentDockVisible] = useState(true);
  const [alignmentDockPos, setAlignmentDockPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [biteViewMode, setBiteViewMode] = useState<BiteViewMode>("optimized");
  const [originalSnapshot, setOriginalSnapshot] = useState<OriginalBiteSnapshot | null>(null);
  const [biteRegistration, setBiteRegistration] = useState<BiteRegistrationInfo | null>(null);
  const [optimizingBite, setOptimizingBite] = useState(false);
  const [optimizeProgress, setOptimizeProgress] = useState<BiteOptimizeProgress | null>(
    null
  );
  const [taskProgress, setTaskProgress] = useState<{
    label: string;
    done: number;
    total: number;
  } | null>(null);
  const [optimizeQuality, setOptimizeQuality] =
    useState<BiteOptimizeQuality>("fast");
  const [archMode, setArchMode] = useState<"auto" | "full" | "partial">("auto");
  const [segmentSide, setSegmentSide] = useState<SegmentSide>("auto");
  const [resolvedArchKind, setResolvedArchKind] = useState<ArchScanKind | null>(null);
  const [archProfiles, setArchProfiles] = useState<{
    upper: ArchScanProfile;
    lower: ArchScanProfile;
  } | null>(null);
  const [undoStack, setUndoStack] = useState<PoseSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<PoseSnapshot[]>([]);
  const [peekOriginal, setPeekOriginal] = useState(false);
  const [lastQualityScore, setLastQualityScore] = useState<number | null>(null);
  const [meshes, setMeshes] = useState<OcclusionViewerMeshes>({
    upper: null,
    lower: null,
    bite: null,
    showBite: false,
  });

  const canAnalyze =
    inputMode === "combined"
      ? Boolean(slots.combined.file)
      : Boolean(slots.upper.file && slots.lower.file && (skipBite || slots.bite.file));

  const isPartialArch = resolvedArchKind === "partial_segment";

  const selectedOption = biteOptions.find((o) => o.id === selectedOptionId) ?? null;
  /** True once the pose has been nudged away from the selected option. */
  const editedFromOption =
    selectedOption != null &&
    (jawTransformKey(selectedOption.upperTx) !== jawTransformKey(upperTx) ||
      jawTransformKey(selectedOption.lowerTx) !== jawTransformKey(lowerTx));

  const movableJaw: ActiveJaw =
    lockedJaw === "upper" ? "lower" : lockedJaw === "lower" ? "upper" : activeJaw;
  const displayJaw: ActiveJaw = lockedJaw === "none" ? activeJaw : movableJaw;
  const activeTx = displayJaw === "upper" ? upperTx : lowerTx;
  const displayUpperTx = useMemo(
    () => deltaJawTransform(bakedUpperTx, upperTx),
    [bakedUpperTx, upperTx]
  );
  const displayLowerTx = useMemo(
    () => deltaJawTransform(bakedLowerTx, lowerTx),
    [bakedLowerTx, lowerTx]
  );
  const precisionMode = lockedJaw !== "none";
  const activePriorityRegions =
    priorityEnabled && priorityRegions.length > 0 ? priorityRegions : undefined;

  const jawDragConfig = useMemo(
    () =>
      lockedJaw !== "none" && !excludePaintMode && !priorityPaintMode
        ? {
            enabled: true,
            jaw: movableJaw,
            transform: movableJaw === "upper" ? upperTx : lowerTx,
            translateSensitivity: 0.0003,
            rotateSensitivity: 0.03,
          }
        : null,
    [lockedJaw, excludePaintMode, priorityPaintMode, movableJaw, upperTx, lowerTx]
  );

  useEffect(() => {
    if (!exportMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.("[data-export-menu]")) return;
      setExportMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [exportMenuOpen]);

  useEffect(() => {
    document.title = "Bite Finder";
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
      if (recomputeTimer.current) clearTimeout(recomputeTimer.current);
    };
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = async () => {
    const el = shellRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) await el.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      /* ignore */
    }
  };

  const disposeOriginalSnapshot = () => {
    setOriginalSnapshot((prev) => {
      if (prev) {
        prev.upper.dispose();
        prev.lower.dispose();
      }
      return null;
    });
  };

  const disposeBases = () => {
    baseUpperRef.current?.dispose();
    baseLowerRef.current?.dispose();
    biteRef.current?.dispose();
    baseUpperRef.current = null;
    baseLowerRef.current = null;
    baseLowerBvhRef.current = null;
    baseUpperBvhRef.current = null;
    biteRef.current = null;
    frameRef.current = null;
  };

  const disposeMeshes = (m: OcclusionViewerMeshes) => {
    // Base copies may be shared — only dispose display geometries distinct from bases
    if (m.upper && m.upper !== baseUpperRef.current) m.upper.dispose();
    if (m.lower && m.lower !== baseLowerRef.current) m.lower.dispose();
    // bite displayed is biteRef — don't dispose here
  };

  const runContactPass = useCallback(
    async (
      upperTxIn: JawTransform,
      lowerTxIn: JawTransform,
      opts: {
        highRes: boolean;
        silent?: boolean;
        captureAsOriginal?: boolean;
        /** Overrides arch-kind state that may not have flushed yet. */
        partial?: boolean;
        onHeatMapProgress?: (done: number, total: number) => void;
      }
    ) => {
      const baseU = baseUpperRef.current;
      const baseL = baseLowerRef.current;
      if (!baseU || !baseL) return;

      const token = ++recomputeToken.current;
      if (!opts.silent) {
        setRecomputing(true);
        setTaskProgress({ label: "Contact heat map", done: 0, total: 100 });
      }

      await yieldToUi();
      if (token !== recomputeToken.current) return;

      const lowerBvh = baseLowerBvhRef.current ?? buildBvh(baseL);
      baseLowerBvhRef.current = lowerBvh;

      let upperBvh = baseUpperBvhRef.current;
      upperBvh = upperBvh ?? buildBvh(baseU);
      baseUpperBvhRef.current = upperBvh;

      const upperM = jawTransformToMatrix(upperTxIn);
      const lowerM = jawTransformToMatrix(lowerTxIn);

      const result = await analyzeOcclusalContacts(
        baseU,
        baseL,
        lowerBvh,
        upperM,
        lowerM,
        {
          contactMm: CONTACT_MM,
          nearContactMm: NEAR_CONTACT_MM,
          lightContactMm: LIGHT_CONTACT_MM,
          maxVertices: opts.highRes ? 60_000 : 14_000,
          maxLowerVertices: opts.highRes ? 30_000 : 7_000,
          colorLowerJaw: true,
          upperBvh: upperBvh ?? undefined,
          exclusionRegions: excludePeaksEnabled ? exclusionRegions : undefined,
          partial: opts.partial ?? isPartialArch,
          // Heat map stays visual; priorities steer auto-find / adjustments only.
          onProgress: (done, total) => {
            opts.onHeatMapProgress?.(done, total);
            if (!opts.silent) {
              setTaskProgress({ label: "Contact heat map", done, total });
            }
          },
        }
      );

      if (token !== recomputeToken.current) {
        result.coloredUpper.dispose();
        result.coloredLower.dispose();
        return;
      }

      applyMatrixToGeometryLite(result.coloredUpper, upperM);
      applyMatrixToGeometryLite(result.coloredLower, lowerM);

      setMeshes((prev) => {
        if (prev.upper && prev.upper !== baseUpperRef.current) prev.upper.dispose();
        if (prev.lower && prev.lower !== baseLowerRef.current) prev.lower.dispose();
        return {
          upper: result.coloredUpper,
          lower: result.coloredLower,
          bite: biteRef.current,
          showBite,
        };
      });

      setStats(result.stats);
      setLastQualityScore(estimateBiteQualityFromStats(result.stats));
      setBakedUpperTx(upperTxIn);
      setBakedLowerTx(lowerTxIn);
      setRecomputing(false);
      if (!opts.silent) setTaskProgress(null);

      if (opts.captureAsOriginal) {
        setOriginalSnapshot((prev) => {
          prev?.upper.dispose();
          prev?.lower.dispose();
          return {
            upper: result.coloredUpper.clone(),
            lower: result.coloredLower.clone(),
            stats: { ...result.stats },
          };
        });
      }

      if (!opts.silent) {
        setProgress(
          `Updated · min ${result.stats.minSignedDistance.toFixed(3)} mm`
        );
      }
    },
    [showBite, excludePeaksEnabled, exclusionRegions, isPartialArch]
  );

  const scheduleRecompute = useCallback(
    (u: JawTransform, l: JawTransform) => {
      if (!baseUpperRef.current || !baseLowerRef.current) return;
      if (recomputeTimer.current) clearTimeout(recomputeTimer.current);
      setRecomputing(true);
      setProgress("Updating contacts for custom alignment…");
      recomputeTimer.current = setTimeout(() => {
        void runContactPass(u, l, { highRes: false });
      }, 420);
    },
    [runContactPass]
  );

  useEffect(() => {
    if (stage !== "done" || !baseUpperRef.current || !baseLowerRef.current || recomputing) {
      return;
    }

    const timer = setTimeout(() => {
      const baseL = baseLowerRef.current;
      if (!baseUpperRef.current || !baseL) return;
      const lowerBvh =
        baseLowerBvhRef.current ?? buildBvh(baseL);
      baseLowerBvhRef.current = lowerBvh;

      const adjustments = findContactAdjustments(
        baseUpperRef.current!,
        baseLowerRef.current!,
        lowerBvh,
        upperTx,
        lowerTx,
        lockedJaw === "upper" ? "lower" : lockedJaw === "lower" ? "upper" : activeJaw,
        {
          translateStepMm: nudgeStep,
          rotateStepDeg: rotStep,
          exclusionRegions: excludePeaksEnabled ? exclusionRegions : undefined,
          priorityRegions: activePriorityRegions,
          maxVertices: isPartialArch ? 1400 : 900,
          partial: isPartialArch,
        }
      );
      setContactAdjustments(adjustments);
      setSelectedAdjustmentId((cur) => {
        if (cur && adjustments.some((a) => a.id === cur)) return cur;
        return null;
      });
    }, 220);

    return () => clearTimeout(timer);
  }, [
    stage,
    upperTx,
    lowerTx,
    lockedJaw,
    activeJaw,
    nudgeStep,
    rotStep,
    excludePeaksEnabled,
    exclusionRegions,
    activePriorityRegions,
    recomputing,
    isPartialArch,
  ]);

  const pushHistory = useCallback(() => {
    setUndoStack((prev) => [
      ...prev.slice(-48),
      { upper: { ...upperTx }, lower: { ...lowerTx } },
    ]);
    setRedoStack([]);
  }, [upperTx, lowerTx]);

  const undoPose = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setRedoStack((r) => [...r, { upper: { ...upperTx }, lower: { ...lowerTx } }]);
      setUpperTx(last.upper);
      setLowerTx(last.lower);
      scheduleRecompute(last.upper, last.lower);
      return prev.slice(0, -1);
    });
  }, [upperTx, lowerTx, scheduleRecompute]);

  const redoPose = useCallback(() => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const next = prev[prev.length - 1];
      setUndoStack((u) => [...u, { upper: { ...upperTx }, lower: { ...lowerTx } }]);
      setUpperTx(next.upper);
      setLowerTx(next.lower);
      scheduleRecompute(next.upper, next.lower);
      return prev.slice(0, -1);
    });
  }, [upperTx, lowerTx, scheduleRecompute]);

  const patchActive = (patch: Partial<JawTransform>, recordHistory = false) => {
    if (recordHistory) pushHistory();
    const jaw = lockedJaw === "none" ? activeJaw : movableJaw;
    const baseTx = jaw === "upper" ? upperTx : lowerTx;
    const next = { ...baseTx, ...patch };
    if (jaw === "upper") setUpperTx(next);
    else setLowerTx(next);
    const u = jaw === "upper" ? next : upperTx;
    const l = jaw === "lower" ? next : lowerTx;
    scheduleRecompute(u, l);
  };

  const setJawLock = (lock: LockedJaw) => {
    setLockedJaw(lock);
    if (lock === "upper") setActiveJaw("lower");
    else if (lock === "lower") setActiveJaw("upper");
    if (lock !== "none") {
      setNudgeStep(PRECISION_MM);
      setRotStep(PRECISION_DEG);
    }
    if (excludePaintMode) setExcludePaintMode(false);
    if (priorityPaintMode) setPriorityPaintMode(false);
  };

  const addExclusionRegion = useCallback(
    (center: { x: number; y: number; z: number }) => {
      if (!excludePeaksEnabled) return;
      const now = Date.now();
      if (now - lastExclusionPaintRef.current < 60) return;
      lastExclusionPaintRef.current = now;

      const id = exclusionIdRef.current++;
      setExclusionRegions((prev) => [
        ...prev,
        { id, center, radiusMm: excludeBrushMm },
      ]);
    },
    [excludePeaksEnabled, excludeBrushMm]
  );

  const clearExclusions = () => {
    setExclusionRegions([]);
  };

  const addPriorityRegion = useCallback(
    (center: { x: number; y: number; z: number }) => {
      if (!priorityEnabled) return;
      const now = Date.now();
      if (now - lastPriorityPaintRef.current < 60) return;
      lastPriorityPaintRef.current = now;

      const id = priorityIdRef.current++;
      setPriorityRegions((prev) => [
        ...prev,
        { id, center, radiusMm: priorityBrushMm },
      ]);
    },
    [priorityEnabled, priorityBrushMm]
  );

  const clearPriorities = () => {
    setPriorityRegions([]);
  };

  const togglePriorityPaint = () => {
    setPriorityPaintMode((v) => {
      const next = !v;
      if (next) setExcludePaintMode(false);
      return next;
    });
  };

  const toggleExcludePaint = () => {
    setExcludePaintMode((v) => {
      const next = !v;
      if (next) setPriorityPaintMode(false);
      return next;
    });
  };

  useEffect(() => {
    if (!excludePeaksEnabled) {
      setExcludePaintMode(false);
      if (exclusionRegions.length > 0) {
        setExclusionRegions([]);
      }
    }
  }, [excludePeaksEnabled, exclusionRegions.length]);

  useEffect(() => {
    if (!priorityEnabled) {
      setPriorityPaintMode(false);
    }
  }, [priorityEnabled]);

  useEffect(() => {
    if (!baseUpperRef.current || !baseLowerRef.current || stage !== "done") return;
    if (!excludePeaksEnabled) return;
    scheduleRecompute(upperTx, lowerTx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exclusionRegions]);

  // Viewer uses baked meshes → keep display transforms at identity.
  // Live nudge preview: temporarily show base + group offset requires base meshes.
  // Simpler: schedule only and also pass identity; visual update comes from recompute.

  const nudge = (axis: keyof JawTransform, dir: 1 | -1) => {
    const isRot = axis === "rx" || axis === "ry" || axis === "rz";
    const step = isRot ? rotStep : nudgeStep;
    const jaw = lockedJaw === "none" ? activeJaw : movableJaw;
    const tx = jaw === "upper" ? upperTx : lowerTx;
    const decimals = isRot ? 3 : precisionMode ? 4 : 3;
    patchActive(
      {
        [axis]: Number((tx[axis] + dir * step).toFixed(decimals)),
      },
      true
    );
  };

  const handleJawDrag = useCallback(
    (transform: JawTransform) => {
      if (movableJaw === "upper") setUpperTx(transform);
      else setLowerTx(transform);
    },
    [movableJaw]
  );

  const handleJawDragEnd = useCallback(
    (transform: JawTransform) => {
      const u = movableJaw === "upper" ? transform : upperTx;
      const l = movableJaw === "lower" ? transform : lowerTx;
      scheduleRecompute(u, l);
    },
    [movableJaw, upperTx, lowerTx, scheduleRecompute]
  );

  const applyAdjustment = useCallback(
    (adj: ContactAdjustment) => {
      pushHistory();
      const jaw = lockedJaw === "none" ? activeJaw : movableJaw;
      const tx = jaw === "upper" ? upperTx : lowerTx;
      const next = {
        ...tx,
        [adj.axis]: Number((tx[adj.axis] + adj.direction * adj.step).toFixed(3)),
      };
      if (jaw === "upper") setUpperTx(next);
      else setLowerTx(next);
      scheduleRecompute(jaw === "upper" ? next : upperTx, jaw === "lower" ? next : lowerTx);
      setSelectedAdjustmentId(adj.id);
      if (lockedJaw === "none") setActiveJaw(jaw);
    },
    [lockedJaw, activeJaw, movableJaw, upperTx, lowerTx, scheduleRecompute, pushHistory]
  );

  const resetTransforms = () => {
    pushHistory();
    setUpperTx(IDENTITY_JAW);
    setLowerTx(IDENTITY_JAW);
    setBakedUpperTx(IDENTITY_JAW);
    setBakedLowerTx(IDENTITY_JAW);
    setLockedJaw("none");
    setExcludePeaksEnabled(false);
    setExcludePaintMode(false);
    setExclusionRegions([]);
    setPriorityPaintMode(false);
    setPriorityRegions([]);
    scheduleRecompute(IDENTITY_JAW, IDENTITY_JAW);
  };

  const onFile = useCallback((slot: Slot, e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSlots((prev) => ({
      ...prev,
      [slot]: { file, name: file?.name ?? "" },
    }));
    setStage("idle");
    setStats(null);
    setContactAdjustments([]);
    setSelectedAdjustmentId(null);
    setError(null);
  }, []);

  const clearSlot = (slot: Slot) => {
    setSlots((prev) => ({ ...prev, [slot]: { file: null, name: "" } }));
    setStage("idle");
    setStats(null);
    setContactAdjustments([]);
    setSelectedAdjustmentId(null);
  };

  const resetAll = () => {
    if (recomputeTimer.current) clearTimeout(recomputeTimer.current);
    setMeshes((prev) => {
      disposeMeshes(prev);
      return { upper: null, lower: null, bite: null, showBite: false };
    });
    disposeBases();
    disposeOriginalSnapshot();
    setBiteRegistration(null);
    setBiteViewMode("optimized");
    setOptimizingBite(false);
    setOptimizeProgress(null);
    setTaskProgress(null);
    setSlots(emptySlots);
    setStats(null);
    setContactAdjustments([]);
    setSelectedAdjustmentId(null);
    setUpperTx(IDENTITY_JAW);
    setLowerTx(IDENTITY_JAW);
    setBakedUpperTx(IDENTITY_JAW);
    setBakedLowerTx(IDENTITY_JAW);
    setLockedJaw("none");
    setExcludePeaksEnabled(false);
    setExcludePaintMode(false);
    setExclusionRegions([]);
    setPriorityPaintMode(false);
    setPriorityRegions([]);
    setError(null);
    setStage("idle");
    setProgress("");
    setUndoStack([]);
    setRedoStack([]);
    setLastQualityScore(null);
    setPeekOriginal(false);
    setResolvedArchKind(null);
    setArchProfiles(null);
    setWarnings([]);
    setBiteOptions([]);
    setSelectedOptionId(null);
    setSwapCombined(false);
  };

  const cancelAutoFind = useCallback(() => {
    optimizeToken.current += 1;
    setOptimizingBite(false);
    setOptimizeProgress(null);
    setTaskProgress(null);
    setProgress("Auto-find cancelled");
  }, []);

  const exportAlignment = useCallback(() => {
    const quality = stats ? estimateBiteQualityFromStats(stats) : lastQualityScore;
    const frame = frameRef.current;
    const worldFromBite = frame?.worldFromBite ?? new THREE.Matrix4();
    const biteFromWorld = frame?.biteFromWorld ?? new THREE.Matrix4();
    const fileToFinal = (tx: JawTransform, registration: THREE.Matrix4) =>
      matrixToRowMajor(
        new THREE.Matrix4()
          .copy(worldFromBite)
          .multiply(jawTransformToMatrix(tx))
          .multiply(biteFromWorld)
          .multiply(registration)
      );
    const payload = {
      app: "Bite Finder",
      exportedAt: new Date().toISOString(),
      qualityScore: quality,
      /** Row-major 4×4: original STL coordinates → final bite pose in the scanner frame. */
      upperFileToFinal: fileToFinal(upperTx, registrationRef.current.upper),
      lowerFileToFinal: fileToFinal(lowerTx, registrationRef.current.lower),
      biteFrame: frame
        ? {
            method: frame.method,
            anteriorResolved: frame.anteriorResolved,
            worldFromBite: matrixToRowMajor(frame.worldFromBite),
          }
        : null,
      selectedBiteOption: selectedOptionId,
      editedAfterOption: editedFromOption,
      biteOptions: biteOptions.map(({ id, qualityScore, contactAreaMm2, finalMinGap }) => ({
        id,
        qualityScore,
        contactAreaMm2,
        finalMinGap,
      })),
      /** Manual/optimized offsets in the bite frame (mm, degrees). */
      upperTransformMmDeg: upperTx,
      lowerTransformMmDeg: lowerTx,
      stats,
      biteRegistration,
      optimizeQuality,
      archMode,
      segmentSide,
      archKind: resolvedArchKind,
      archProfiles,
      priorityRegions: activePriorityRegions ?? [],
      exclusionRegions: excludePeaksEnabled ? exclusionRegions : [],
    };
    const stamp = Date.now();
    triggerDownload(
      `bite-alignment-${stamp}.json`,
      new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
    );
    setExportMenuOpen(false);
  }, [stats, lastQualityScore, upperTx, lowerTx, biteRegistration, optimizeQuality, archMode, segmentSide, resolvedArchKind, archProfiles, activePriorityRegions, excludePeaksEnabled, exclusionRegions, selectedOptionId, editedFromOption, biteOptions]);

  const exportOptimizedStl = useCallback(
    (which: "upper" | "lower" | "both") => {
      const stamp = Date.now();
      const exportOne = (jaw: "upper" | "lower") => {
        const base = jaw === "upper" ? baseUpperRef.current : baseLowerRef.current;
        if (!base) return false;
        const tx = jaw === "upper" ? upperTx : lowerTx;
        const geo = base.clone();
        const toScanner = new THREE.Matrix4()
          .copy(frameRef.current?.worldFromBite ?? new THREE.Matrix4())
          .multiply(jawTransformToMatrix(tx));
        applyMatrixToGeometryLite(geo, toScanner);
        try {
          triggerDownload(
            `optimized-${jaw}-${stamp}.stl`,
            geometryToBinaryStlBlob(geo)
          );
          return true;
        } finally {
          geo.dispose();
        }
      };

      let ok = false;
      if (which === "upper" || which === "both") ok = exportOne("upper") || ok;
      if (which === "lower" || which === "both") ok = exportOne("lower") || ok;
      if (ok) {
        setProgress(
          which === "both"
            ? "Exported optimized upper & lower STL"
            : `Exported optimized ${which} STL`
        );
      } else {
        setProgress("Nothing to export — run analysis first");
      }
      setExportMenuOpen(false);
    },
    [upperTx, lowerTx]
  );

  const autoFindOptimalBite = useCallback(async () => {
    const baseU = baseUpperRef.current;
    const baseL = baseLowerRef.current;
    if (!baseU || !baseL || stage !== "done") return;

    const effectiveQuality =
      activePriorityRegions != null ? "accurate" : optimizeQuality;

    pushHistory();
    const token = ++optimizeToken.current;
    setOptimizingBite(true);
    setOptimizeProgress(null);
    setTaskProgress({ label: "Searching optimal bite closure", done: 0, total: 40 });
    setProgress(
      `Auto-finding ${isPartialArch ? "segmental" : ""} bite (${effectiveQuality}${
        activePriorityRegions ? ", priority zones" : ""
      })…`
    );
    setBiteViewMode("optimized");

    const movable: ActiveJaw =
      lockedJaw === "upper" ? "lower" : lockedJaw === "lower" ? "upper" : "upper";
    if (lockedJaw === "none") {
      setLockedJaw("lower");
      setActiveJaw("upper");
    }
    setPriorityPaintMode(false);
    setExcludePaintMode(false);

    const lowerBvh = baseLowerBvhRef.current ?? buildBvh(baseL);
    baseLowerBvhRef.current = lowerBvh;

    try {
      const options = await findBiteOptions(baseU, baseL, lowerBvh, upperTx, lowerTx, movable, {
        count: 5,
        quality: effectiveQuality,
        partial: isPartialArch,
        exclusionRegions: excludePeaksEnabled ? exclusionRegions : undefined,
        priorityRegions: activePriorityRegions,
        shouldCancel: () => token !== optimizeToken.current,
        onProgress: (p) => {
          if (token !== optimizeToken.current) return;
          const label =
            p.stage === "explore"
              ? `Exploring bite ${p.done + 1}/${p.total}`
              : `Refining option ${Math.min(p.done + 1, p.total)}/${p.total}`;
          setTaskProgress({ label, done: p.done, total: p.total });
          setProgress(
            `${label}${p.bestQuality != null ? ` · best quality so far ${p.bestQuality}` : ""}`
          );
        },
      });

      if (token !== optimizeToken.current) return;
      if (options.length === 0) {
        setProgress("No seated bite found — check that the arches face each other");
        return;
      }

      const result = options[0];
      setBiteOptions(options);
      setSelectedOptionId(result.id);
      setUpperTx(result.upperTx);
      setLowerTx(result.lowerTx);
      setLastQualityScore(result.qualityScore);
      setTaskProgress({ label: "Rendering contact heat map", done: 0, total: 100 });
      setProgress("Rendering optimized contact map…");

      await runContactPass(result.upperTx, result.lowerTx, {
        highRes: true,
        onHeatMapProgress: (done, total) => {
          if (token !== optimizeToken.current) return;
          setTaskProgress({
            label: "Rendering contact heat map",
            done,
            total,
          });
        },
      });

      if (token !== optimizeToken.current) return;

      setBiteViewMode("compare");
      setProgress(
        `Found ${options.length} bite option${options.length === 1 ? "" : "s"} · showing #1 (quality ${
          result.qualityScore
        }/100, ${result.contactAreaMm2.toFixed(1)} mm² contact)`
      );
      requestAnimationFrame(() => viewerRef.current?.zoomOutOverview());
    } finally {
      if (token === optimizeToken.current) {
        setOptimizingBite(false);
        setOptimizeProgress(null);
        setTaskProgress(null);
      }
    }
  }, [
    stage,
    lockedJaw,
    upperTx,
    lowerTx,
    excludePeaksEnabled,
    exclusionRegions,
    activePriorityRegions,
    runContactPass,
    optimizeQuality,
    pushHistory,
    isPartialArch,
  ]);

  const applyBiteOption = useCallback(
    (option: BiteOption) => {
      if (recomputeTimer.current) clearTimeout(recomputeTimer.current);
      pushHistory();
      setSelectedOptionId(option.id);
      setUpperTx(option.upperTx);
      setLowerTx(option.lowerTx);
      setLastQualityScore(option.qualityScore);
      setBiteViewMode("optimized");
      void runContactPass(option.upperTx, option.lowerTx, { highRes: true });
    },
    [pushHistory, runContactPass]
  );
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (stage !== "done") return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if (e.code === "Space" && originalSnapshot && !e.repeat) {
        e.preventDefault();
        setPeekOriginal(true);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        undoPose();
        return;
      }
      if (
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "z")
      ) {
        e.preventDefault();
        redoPose();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "e") {
        e.preventDefault();
        exportAlignment();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Space") setPeekOriginal(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [stage, originalSnapshot, undoPose, redoPose, exportAlignment]);

  const runAnalysis = async (opts: { swap?: boolean } = {}) => {
    const combinedMode = inputMode === "combined";
    const swap = opts.swap ?? swapCombined;
    if (combinedMode) {
      if (!slots.combined.file) return;
    } else {
      if (!slots.upper.file || !slots.lower.file) return;
      if (!skipBite && !slots.bite.file) return;
    }
    const useBite = !combinedMode && !skipBite;

    if (recomputeTimer.current) clearTimeout(recomputeTimer.current);
    setMeshes((prev) => {
      disposeMeshes(prev);
      return { upper: null, lower: null, bite: null, showBite };
    });
    disposeBases();
    disposeOriginalSnapshot();
    setBiteRegistration(null);
    setBiteViewMode("optimized");
    setOptimizingBite(false);
    setOptimizeProgress(null);
    setTaskProgress(null);
    setResolvedArchKind(null);
    setArchProfiles(null);
    setError(null);
    setWarnings([]);
    setStats(null);
    setContactAdjustments([]);
    setSelectedAdjustmentId(null);
    setUpperTx(IDENTITY_JAW);
    setLowerTx(IDENTITY_JAW);
    setBakedUpperTx(IDENTITY_JAW);
    setBakedLowerTx(IDENTITY_JAW);
    setLockedJaw("none");
    setExcludePeaksEnabled(false);
    setExcludePaintMode(false);
    setExclusionRegions([]);
    setPriorityPaintMode(false);
    setPriorityRegions([]);
    setBiteOptions([]);
    setSelectedOptionId(null);
    setStage("loading");
    setProgress("Loading STL files…");

    try {
      const scanWarnings: string[] = [];
      let upper: THREE.BufferGeometry;
      let lower: THREE.BufferGeometry;
      if (combinedMode) {
        const raw = await loadRawStl(slots.combined.file!);
        setProgress("Separating upper and lower arches…");
        await yieldToUi();
        const welded = weldGeometry(raw);
        raw.dispose();
        const split = splitCombinedJaws(welded);
        welded.dispose();
        if (!split) {
          throw new Error(
            "Couldn't separate the combined STL into two arches. Export upper and lower as separate files, or as one file with the arches as separate shells."
          );
        }
        upper = swap ? split.lower : split.upper;
        lower = swap ? split.upper : split.lower;
        if (split.method === "plane") {
          scanWarnings.push(
            "Arches were fused in the combined file — split along the thinnest slab between them. Check the upper/lower colours and swap if needed."
          );
        }
      } else {
        upper = await loadGeometry(slots.upper.file!);
        setProgress("Loading lower arch…");
        lower = await loadGeometry(slots.lower.file!);
      }
      for (const [geo, label] of [
        [upper, "Upper scan"],
        [lower, "Lower scan"],
      ] as const) {
        const w = checkScanUnits(geo, label);
        if (w) scanWarnings.push(w);
      }

      const resolved = resolveArchKind(upper, lower, archMode);
      const partial = resolved.kind === "partial_segment";
      if (partial) {
        setProgress(
          `Segmental scan detected (${resolved.upperProfile.spanX.toFixed(0)}×${resolved.upperProfile.spanY.toFixed(0)} mm) — bite-splint mode…`
        );
        await yieldToUi();
      }

      let bite: THREE.BufferGeometry | null = null;
      let registration: BiteRegistrationInfo = {
        upperRms: 0,
        lowerRms: 0,
        upperOverlap: 0,
        lowerOverlap: 0,
        upperStart: "",
        lowerStart: "",
        skipped: true,
      };
      registrationRef.current = { upper: new THREE.Matrix4(), lower: new THREE.Matrix4() };
      if (useBite && slots.bite.file) {
        setProgress(
          partial
            ? "Loading segmental bite / occlusion scan…"
            : "Loading bite / occlusion scan…"
        );
        bite = await loadGeometry(slots.bite.file);
        const w = checkScanUnits(bite, "Bite scan");
        if (w) scanWarnings.push(w);
      }

      if (useBite && bite) {
        setStage("registering");
        setProgress("Building spatial index on bite scan…");
        await yieldToUi();
        const biteBvh = buildBvh(bite);
        const maxCorr = partial
          ? Math.max(
              4,
              Math.min(12, Math.max(resolved.upperProfile.diagonal, resolved.lowerProfile.diagonal) * 0.35)
            )
          : undefined;

        const registerArch = async (geo: THREE.BufferGeometry, label: "Upper" | "Lower") => {
          const run = (allowGlobalSearch?: boolean) =>
            registerMeshToTarget(
              geo,
              biteBvh,
              bite!,
              {
                sampleCount: partial ? 2400 : 3000,
                maxIterations: partial ? 70 : 60,
                maxCorrespondenceDistance: maxCorr,
                allowGlobalSearch,
                onStage: (msg) => setProgress(`${label} → bite · ${msg}`),
                onProgress: (it, rms) => {
                  setProgress(`${label} → bite · iter ${it} · RMS ${rms.toFixed(4)} mm`);
                },
              },
              false
            );
          let result = await run();
          if (result.inlierFraction < LOW_OVERLAP) {
            const deep = await run(true);
            if (deep.inlierFraction > result.inlierFraction) result = deep;
          }
          applyMatrixToGeometryLite(geo, result.transform);
          return result;
        };

        setProgress(
          partial ? "Registering upper segment to bite…" : "Registering upper arch to bite record…"
        );
        await yieldToUi();
        const upperIcp = await registerArch(upper, "Upper");

        setProgress(
          partial ? "Registering lower segment to bite…" : "Registering lower arch to bite record…"
        );
        await yieldToUi();
        const lowerIcp = await registerArch(lower, "Lower");

        registrationRef.current = { upper: upperIcp.transform, lower: lowerIcp.transform };
        registration = {
          upperRms: upperIcp.inlierRms,
          lowerRms: lowerIcp.inlierRms,
          upperOverlap: upperIcp.inlierFraction,
          lowerOverlap: lowerIcp.inlierFraction,
          upperStart: upperIcp.start,
          lowerStart: lowerIcp.start,
          skipped: false,
        };
        if (upperIcp.inlierFraction < LOW_OVERLAP || lowerIcp.inlierFraction < LOW_OVERLAP) {
          scanWarnings.push(
            `Low bite-record overlap (upper ${(upperIcp.inlierFraction * 100).toFixed(1)}%, lower ${(
              lowerIcp.inlierFraction * 100
            ).toFixed(1)}%) — registration may be wrong; check the bite scan covers the buccal surfaces of both arches.`
          );
        }
      }

      // Re-express everything in the bite frame (occlusal plane = XY, +Z toward upper).
      setProgress("Fitting occlusal plane…");
      await yieldToUi();
      const frame = computeBiteFrame(upper, lower, buildBvh(lower));
      applyMatrixToGeometryLite(upper, frame.biteFromWorld);
      applyMatrixToGeometryLite(lower, frame.biteFromWorld);
      if (bite) applyMatrixToGeometryLite(bite, frame.biteFromWorld);
      frameRef.current = frame;
      if (frame.method === "pca") {
        scanWarnings.push(
          "Arches are not in occlusion in the scan frame — occlusal plane estimated from arch shape; check alignment before trusting contacts."
        );
      }

      const framed = resolveArchKind(upper, lower, archMode);
      setResolvedArchKind(framed.kind);
      setArchProfiles({ upper: framed.upperProfile, lower: framed.lowerProfile });
      setWarnings(scanWarnings);

      baseUpperRef.current = upper;
      baseLowerRef.current = lower;
      baseLowerBvhRef.current = null;
      baseUpperBvhRef.current = null;
      biteRef.current = bite;
      setBiteRegistration(registration);

      setStage("analyzing");
      setProgress(
        partial
          ? "Mapping segmental bite contacts (before)…"
          : "Mapping initial bite contacts (before)…"
      );
      setTaskProgress({ label: "Initial contact heat map", done: 0, total: 100 });
      await runContactPass(IDENTITY_JAW, IDENTITY_JAW, {
        highRes: true,
        silent: true,
        captureAsOriginal: true,
        partial: framed.kind === "partial_segment",
        onHeatMapProgress: (done, total) => {
          setTaskProgress({ label: "Initial contact heat map", done, total });
        },
      });
      setTaskProgress(null);
      setLockedJaw("lower");
      setActiveJaw("upper");
      setNudgeStep(partial ? 0.015 : PRECISION_MM);
      setRotStep(partial ? 0.08 : PRECISION_DEG);
      setStage("done");
      const sideHint =
        segmentSide !== "auto"
          ? segmentSide
          : framed.upperProfile.suggestedSide !== "unknown" && frame.anteriorResolved
            ? framed.upperProfile.suggestedSide
            : null;
      setProgress(
        partial
          ? `Segmental bite ready${sideHint ? ` · ${sideHint}` : ""} — auto-find seats the available teeth for a local splint.`
          : registration.skipped
            ? "Bite found — fix lower jaw, then optimize or tune manually."
            : `Bite registered (fit ${registration.upperRms.toFixed(3)} / ${registration.lowerRms.toFixed(3)} mm, overlap ${(
                registration.upperOverlap * 100
              ).toFixed(0)}% / ${(registration.lowerOverlap * 100).toFixed(0)}%) — optimize closure or compare before/after.`
      );
      requestAnimationFrame(() => viewerRef.current?.zoomOutOverview());
    } catch (err) {
      console.error(err);
      setStage("error");
      setError(
        err instanceof Error
          ? err.message
          : "Failed to analyze meshes. Check that files are valid STL."
      );
      setProgress("");
      setRecomputing(false);
    }
  };

  const selectAdjustment = (adj: ContactAdjustment) => {
    setSelectedAdjustmentId(adj.id);
  };

  const showOverview = () => {
    setBiteViewMode("optimized");
    setSelectedAdjustmentId(null);
    setShowAllAdjustments(true);
    requestAnimationFrame(() => viewerRef.current?.zoomOutOverview());
  };

  const selectedAdjustment = useMemo(
    () => contactAdjustments.find((a) => a.id === selectedAdjustmentId) ?? null,
    [contactAdjustments, selectedAdjustmentId]
  );

  const guidanceJaw: ActiveJaw =
    lockedJaw === "upper" ? "lower" : lockedJaw === "lower" ? "upper" : activeJaw;

  const legend = useMemo(
    () => [
      {
        color: "#b80d1a",
        label: `Interference — teeth overlapping by > ${PENETRATION_TOLERANCE_MM} mm (too tight)`,
      },
      { color: "#f97316", label: `Max contact (≤ ${CONTACT_MM} mm)` },
      { color: "#eab308", label: `Near / light contact (≤ ${NEAR_CONTACT_MM} mm)` },
      { color: "#3b82f6", label: "Clearance — not touching / open gap" },
    ],
    []
  );

  const qualityScore = useMemo(() => {
    if (lastQualityScore != null) return lastQualityScore;
    if (stats) return estimateBiteQualityFromStats(stats);
    return null;
  }, [lastQualityScore, stats]);

  const qualityTone =
    qualityScore == null
      ? "slate"
      : qualityScore >= 75
        ? "emerald"
        : qualityScore >= 50
          ? "amber"
          : "rose";

  const effectiveViewMode: BiteViewMode =
    peekOriginal && originalSnapshot ? "original" : biteViewMode;

  const customActive = !isIdentity(upperTx) || !isIdentity(lowerTx);

  const originalViewerMeshes = useMemo((): OcclusionViewerMeshes | null => {
    if (!originalSnapshot) return null;
    return {
      upper: originalSnapshot.upper,
      lower: originalSnapshot.lower,
      bite: biteRef.current,
      showBite,
    };
  }, [originalSnapshot, showBite]);

  const biteDelta = useMemo(() => {
    if (!originalSnapshot || !stats) return null;
    return {
      minGapBefore: originalSnapshot.stats.minSignedDistance,
      minGapAfter: stats.minSignedDistance,
      minGapImprovement:
        originalSnapshot.stats.minSignedDistance - stats.minSignedDistance,
      contactBefore: originalSnapshot.stats.contactCount,
      contactAfter: stats.contactCount,
      interferenceBefore: originalSnapshot.stats.interferenceCount,
      interferenceAfter: stats.interferenceCount,
      areaBefore: originalSnapshot.stats.contactAreaMm2,
      areaAfter: stats.contactAreaMm2,
      penetrationBefore: originalSnapshot.stats.maxPenetrationMm,
      penetrationAfter: stats.maxPenetrationMm,
    };
  }, [originalSnapshot, stats]);

  const showInteractiveChrome =
    effectiveViewMode === "optimized" && !optimizingBite && !peekOriginal;

  const loadingOverlay = useMemo(() => {
    if (optimizingBite && (optimizeProgress || taskProgress)) {
      const pct = taskProgress
        ? Math.min(100, Math.round((taskProgress.done / Math.max(1, taskProgress.total)) * 100))
        : optimizeProgress
          ? Math.min(
              100,
              Math.round(
                (optimizeProgress.iteration / Math.max(1, optimizeProgress.maxIterations)) * 100
              )
            )
          : 8;
      return {
        title: "Finding best bite options",
        detail:
          optimizeProgress && optimizeProgress.phase !== "finalize"
            ? `${optimizeProgress.phase} pass · ${optimizeProgress.appliedSteps} moves · gap ${optimizeProgress.minGap.toFixed(3)} mm`
            : taskProgress?.label ?? "Searching…",
        percent: pct,
      };
    }
    if (taskProgress && stage === "analyzing") {
      return {
        title: "Analyzing bite",
        detail: taskProgress.label,
        percent: Math.min(
          100,
          Math.round((taskProgress.done / Math.max(1, taskProgress.total)) * 100)
        ),
      };
    }
    if (stage === "loading") {
      return { title: "Loading scans", detail: progress || "Reading STL files…", percent: 12 };
    }
    if (stage === "registering") {
      return {
        title: "Registering to bite",
        detail: progress || "ICP alignment…",
        percent: 42,
      };
    }
    // Contact refreshes after manual edits use the small pill so the view stays visible.
    return null;
  }, [
    optimizingBite,
    optimizeProgress,
    taskProgress,
    stage,
    progress,
    recomputing,
  ]);

  return (
    <div
      ref={shellRef}
      className="fixed inset-0 z-[100] flex h-[100dvh] w-screen flex-col overflow-hidden bg-[#070b14] text-slate-100"
    >
      {/* Top bar */}
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-[#0b1220]/95 px-4 py-2.5 backdrop-blur">
        <div className="flex min-w-0 items-center gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-[15px] font-semibold tracking-tight text-white">
              Bite Finder
            </h1>
            <p className="truncate text-[11px] text-slate-500">
              Full arch or segmental · bite splint · hold Space for original
            </p>
          </div>
          {qualityScore != null && stage === "done" && (
            <div
              className={`hidden shrink-0 items-center gap-2 rounded-xl border px-2.5 py-1.5 sm:flex ${
                qualityTone === "emerald"
                  ? "border-emerald-500/30 bg-emerald-950/40"
                  : qualityTone === "amber"
                    ? "border-amber-500/30 bg-amber-950/40"
                    : qualityTone === "rose"
                      ? "border-rose-500/30 bg-rose-950/40"
                      : "border-white/10 bg-white/[0.03]"
              }`}
              title="Bite quality score"
            >
              <span className="text-[10px] uppercase tracking-wide text-slate-500">Quality</span>
              <span
                className={`font-mono text-sm font-semibold ${
                  qualityTone === "emerald"
                    ? "text-emerald-300"
                    : qualityTone === "amber"
                      ? "text-amber-300"
                      : qualityTone === "rose"
                        ? "text-rose-300"
                        : "text-slate-300"
                }`}
              >
                {qualityScore}
              </span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {stage === "done" && originalSnapshot && (
            <div className="mr-1 hidden items-center rounded-lg border border-white/10 p-0.5 sm:flex">
              {(
                [
                  ["optimized", "Optimized"],
                  ["original", "Original"],
                  ["compare", "Compare"],
                ] as const
              ).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setBiteViewMode(mode)}
                  className={`rounded-md px-2 py-1 text-[10px] font-medium ${
                    biteViewMode === mode
                      ? "bg-[#1DA8C6] text-white"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {stage === "done" && (
            <>
              <HdrBtn onClick={undoPose} title="Undo (Ctrl+Z)" disabled={undoStack.length === 0}>
                Undo
              </HdrBtn>
              <HdrBtn onClick={redoPose} title="Redo (Ctrl+Y)" disabled={redoStack.length === 0}>
                Redo
              </HdrBtn>
              <div className="relative" data-export-menu>
                <HdrBtn
                  onClick={() => setExportMenuOpen((v) => !v)}
                  title="Export alignment JSON or optimized STL"
                >
                  <FiDownload />
                  <span className="hidden lg:inline">Export</span>
                </HdrBtn>
                {exportMenuOpen && (
                  <div className="absolute right-0 top-full z-30 mt-1 w-52 overflow-hidden rounded-lg border border-white/15 bg-[#0c1220] py-1 shadow-xl">
                    <button
                      type="button"
                      onClick={exportAlignment}
                      className="block w-full px-3 py-2 text-left text-[11px] text-slate-300 hover:bg-white/5 hover:text-white"
                    >
                      Alignment JSON
                    </button>
                    <button
                      type="button"
                      onClick={() => exportOptimizedStl("upper")}
                      className="block w-full px-3 py-2 text-left text-[11px] text-slate-300 hover:bg-white/5 hover:text-white"
                    >
                      Optimized upper STL
                    </button>
                    <button
                      type="button"
                      onClick={() => exportOptimizedStl("lower")}
                      className="block w-full px-3 py-2 text-left text-[11px] text-slate-300 hover:bg-white/5 hover:text-white"
                    >
                      Optimized lower STL
                    </button>
                    <button
                      type="button"
                      onClick={() => exportOptimizedStl("both")}
                      className="block w-full px-3 py-2 text-left text-[11px] text-emerald-300 hover:bg-emerald-950/40 hover:text-emerald-200"
                    >
                      Both optimized STLs
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {stage === "done" && (
            <HdrBtn onClick={showOverview} title="Zoom out">
              <FiZoomOut />
              <span className="hidden sm:inline">Overview</span>
            </HdrBtn>
          )}
          {stage === "done" && (
            <HdrBtn
              onClick={() => setAlignmentDockVisible((v) => !v)}
              title={alignmentDockVisible ? "Hide alignment panel" : "Show alignment panel"}
            >
              <FiMove />
              <span className="hidden sm:inline">
                {alignmentDockVisible ? "Hide align" : "Align"}
              </span>
            </HdrBtn>
          )}
          <HdrBtn onClick={() => setPanelOpen((v) => !v)} title="Panel">
            {panelOpen ? <FiChevronLeft /> : <FiChevronRight />}
          </HdrBtn>
          <HdrBtn onClick={toggleFullscreen} title="Fullscreen">
            {isFullscreen ? <FiMinimize /> : <FiMaximize />}
          </HdrBtn>
          <HdrBtn
            onClick={() => {
              if (window.history.length > 1) window.history.back();
              else window.location.href = "/dashboard";
            }}
            title="Close"
          >
            <FiX />
          </HdrBtn>
        </div>
      </header>

      <div className="relative flex min-h-0 flex-1">
        {/* Side panel */}
        <aside
          className={`absolute inset-y-0 left-0 z-20 flex w-[min(100%,20.5rem)] flex-col overflow-y-auto border-r border-white/10 bg-[#0b1220] p-3.5 shadow-2xl transition-transform duration-300 ${
            panelOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <SectionLabel>Scans</SectionLabel>
          <div className="mb-2 flex gap-1">
            {(
              [
                ["separate", "Upper + lower"],
                ["combined", "Combined STL"],
              ] as const
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                disabled={stage === "loading" || stage === "registering" || stage === "analyzing"}
                onClick={() => {
                  setInputMode(mode);
                  setStage("idle");
                }}
                className={`flex-1 rounded-md px-1.5 py-1.5 text-[10px] font-medium ${
                  inputMode === mode
                    ? "bg-[#1DA8C6] text-white"
                    : "border border-white/10 text-slate-400 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {inputMode === "combined" ? (
            <>
              <SlotUpload
                label="Upper + lower (one file)"
                slot="combined"
                state={slots.combined}
                onFile={onFile}
                onClear={clearSlot}
                accent="#f59e0b"
              />
              <p className="mt-1 text-[10px] leading-snug text-slate-600">
                Both arches exported together in their scanned bite. They are separated automatically.
              </p>
            </>
          ) : (
            <>
              <SlotUpload label="Upper" slot="upper" state={slots.upper} onFile={onFile} onClear={clearSlot} accent="#f87171" />
              <SlotUpload label="Lower" slot="lower" state={slots.lower} onFile={onFile} onClear={clearSlot} accent="#94a3b8" />
              <SlotUpload label="Bite" slot="bite" state={slots.bite} onFile={onFile} onClear={clearSlot} accent="#34d399" disabled={skipBite} />

              <label className="mt-2 flex cursor-pointer items-start gap-2 text-[11px] text-slate-500">
                <input type="checkbox" className="mt-0.5" checked={skipBite} onChange={(e) => setSkipBite(e.target.checked)} />
                No bite scan — upper and lower are already in their scanned position
              </label>
            </>
          )}
          <label className="mt-1.5 flex cursor-pointer items-start gap-2 text-[11px] text-slate-500">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={showBite}
              onChange={(e) => {
                const v = e.target.checked;
                setShowBite(v);
                setMeshes((m) => ({ ...m, showBite: v }));
              }}
            />
            Show bite mesh
          </label>
          <label className="mt-1.5 flex cursor-pointer items-start gap-2 text-[11px] text-slate-500">
            <input type="checkbox" className="mt-0.5" checked={showAllAdjustments} onChange={(e) => setShowAllAdjustments(e.target.checked)} />
            Show all adjustment arrows
          </label>

          <div className="mt-3 rounded-xl border border-cyan-500/20 bg-cyan-950/20 p-2.5">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-cyan-400/90">
              Scan coverage
            </p>
            <p className="mb-2 text-[10px] leading-snug text-slate-500">
              Use partial when you only have a segment — e.g. left upper/lower last 4 biting teeth for a local bite splint.
            </p>
            <div className="mb-2 flex gap-1">
              {(
                [
                  ["auto", "Auto"],
                  ["full", "Full arch"],
                  ["partial", "Partial"],
                ] as const
              ).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  disabled={optimizingBite || stage === "loading" || stage === "registering" || stage === "analyzing"}
                  onClick={() => setArchMode(mode)}
                  className={`flex-1 rounded-md px-1.5 py-1.5 text-[10px] font-medium ${
                    archMode === mode
                      ? "bg-cyan-600 text-white"
                      : "border border-white/10 text-slate-400 hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {(archMode === "partial" || archMode === "auto") && (
              <label className="flex items-center justify-between gap-2 text-[10px] text-slate-500">
                Segment side
                <select
                  className="rounded border border-white/10 bg-[#070b14] px-1.5 py-0.5 text-slate-300"
                  value={segmentSide}
                  onChange={(e) => setSegmentSide(e.target.value as SegmentSide)}
                >
                  <option value="auto">Auto</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                  <option value="anterior">Anterior</option>
                  <option value="posterior">Posterior</option>
                </select>
              </label>
            )}
            {resolvedArchKind && stage === "done" && (
              <p
                className={`mt-2 rounded-md px-2 py-1 text-[10px] ${
                  isPartialArch
                    ? "bg-cyan-500/15 text-cyan-200"
                    : "bg-white/[0.04] text-slate-400"
                }`}
              >
                {isPartialArch ? (
                  <>
                    Segmental mode active
                    {archProfiles
                      ? ` · span ~${Math.max(archProfiles.upper.spanX, archProfiles.upper.spanY).toFixed(0)} mm`
                      : ""}
                    {segmentSide !== "auto" ? ` · ${segmentSide}` : ""}
                    {" — seats available teeth for splint"}
                  </>
                ) : (
                  "Full-arch mode"
                )}
              </p>
            )}
          </div>

          {stage === "done" && (
            <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-2.5">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Auto-find quality
              </p>
              <div className="flex gap-1">
                {(
                  [
                    ["fast", "Fast"],
                    ["balanced", "Balanced"],
                    ["accurate", "Accurate"],
                  ] as const
                ).map(([q, label]) => (
                  <button
                    key={q}
                    type="button"
                    disabled={optimizingBite}
                    onClick={() => setOptimizeQuality(q)}
                    className={`flex-1 rounded-md px-1.5 py-1.5 text-[10px] font-medium ${
                      optimizeQuality === q
                        ? "bg-[#1DA8C6] text-white"
                        : "border border-white/10 text-slate-400 hover:text-white"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-[10px] leading-snug text-slate-600">
                Fast is default (snappy). Accurate adds polish for tougher seats.
                {activePriorityRegions
                  ? " Priority zones force Accurate for max seating precision."
                  : ""}
              </p>
            </div>
          )}

          {stage === "done" && (
            <>
              <SectionLabel className="mt-5">Priority bite zones</SectionLabel>
              <label className="flex cursor-pointer items-start gap-2 text-[11px] text-slate-500">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={priorityEnabled}
                  onChange={(e) => setPriorityEnabled(e.target.checked)}
                />
                Mark where bite contact matters most — auto-find seats these first
              </label>
              {priorityEnabled && (
                <div className="mt-2 space-y-2 rounded-lg border border-amber-500/25 bg-amber-950/20 p-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={togglePriorityPaint}
                      disabled={optimizingBite}
                      className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] ${
                        priorityPaintMode
                          ? "bg-amber-500 text-black"
                          : "border border-amber-500/30 text-amber-200 hover:bg-amber-500/10"
                      }`}
                    >
                      {priorityPaintMode ? "Painting…" : "Paint priority zones"}
                    </button>
                    <button
                      type="button"
                      onClick={clearPriorities}
                      disabled={priorityRegions.length === 0}
                      className="flex items-center gap-1 rounded-md border border-white/10 px-2 py-1.5 text-[11px] text-slate-400 hover:text-white disabled:opacity-40"
                    >
                      <FiTrash2 size={12} />
                      Clear
                    </button>
                  </div>
                  <label className="flex items-center justify-between text-[10px] text-slate-500">
                    Brush size (mm)
                    <select
                      className="rounded border border-white/10 bg-[#070b14] px-1 py-0.5 text-slate-300"
                      value={priorityBrushMm}
                      onChange={(e) => setPriorityBrushMm(Number(e.target.value))}
                    >
                      {BRUSH_SIZE_OPTIONS_MM.map((mm) => (
                        <option key={mm} value={mm}>
                          {mm}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="text-[10px] text-amber-200/70">
                    {priorityRegions.length} priority region
                    {priorityRegions.length === 1 ? "" : "s"} · paint on upper arch
                    before auto-find
                  </p>
                </div>
              )}
            </>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!canAnalyze || stage === "loading" || stage === "registering" || stage === "analyzing"}
              onClick={() => void runAnalysis()}
              className="flex min-w-[7rem] flex-1 items-center justify-center gap-2 rounded-lg bg-[#1DA8C6] px-3 py-2 text-sm font-medium text-white hover:bg-[#1897ad] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {(stage === "loading" || stage === "registering" || stage === "analyzing") && (
                <FiLoader className="animate-spin" />
              )}
              Run analysis
            </button>
            {stage === "done" && !optimizingBite && (
              <button
                type="button"
                disabled={recomputing}
                onClick={() => void autoFindOptimalBite()}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-950/40 px-3 py-2 text-sm font-medium text-emerald-200 hover:bg-emerald-900/50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Find 5 best {isPartialArch ? "splint bites" : "bites"}
              </button>
            )}
            {optimizingBite && (
              <button
                type="button"
                onClick={cancelAutoFind}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-rose-500/40 bg-rose-950/40 px-3 py-2 text-sm font-medium text-rose-200 hover:bg-rose-900/50"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={resetAll}
              className="rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-400 hover:bg-white/5"
            >
              Reset
            </button>
          </div>

          {stage === "done" && inputMode === "combined" && (
            <button
              type="button"
              disabled={optimizingBite || recomputing}
              onClick={() => {
                const next = !swapCombined;
                setSwapCombined(next);
                void runAnalysis({ swap: next });
              }}
              className="mt-2 w-full rounded-lg border border-white/10 px-3 py-1.5 text-[11px] text-slate-400 hover:bg-white/5 hover:text-white disabled:opacity-40"
            >
              Upper and lower are the wrong way round? Swap and re-run
            </button>
          )}

          {stage === "done" && biteOptions.length > 0 && (
            <>
              <SectionLabel className="mt-5">Bite options</SectionLabel>
              <p className="mb-2 text-[10px] leading-snug text-slate-600">
                Ranked by bite quality. Click one to load it, then fine-tune with the nudge
                controls or by dragging the jaw.
                {biteOptions.length < 5
                  ? ` Only ${biteOptions.length} distinct seat${biteOptions.length === 1 ? "" : "s"} found — the others converged to the same bite.`
                  : ""}
              </p>
              <ul className="space-y-1.5">
                {biteOptions.map((option) => {
                  const selected = option.id === selectedOptionId;
                  const o = option.offsetFromBestMm;
                  const offsetParts = [
                    Math.abs(o.x) >= 0.02 ? `${Math.abs(o.x).toFixed(2)} mm ${o.x > 0 ? "right" : "left"}` : null,
                    Math.abs(o.y) >= 0.02 ? `${Math.abs(o.y).toFixed(2)} mm ${o.y > 0 ? "forward" : "back"}` : null,
                    Math.abs(o.z) >= 0.02 ? `${Math.abs(o.z).toFixed(2)} mm ${o.z > 0 ? "more open" : "more closed"}` : null,
                    option.rotationFromBestDeg >= 0.05 ? `${option.rotationFromBestDeg.toFixed(1)}° turned` : null,
                  ].filter(Boolean);
                  return (
                    <li key={option.id}>
                      <button
                        type="button"
                        disabled={optimizingBite || recomputing}
                        onClick={() => applyBiteOption(option)}
                        className={`w-full rounded-lg border p-2 text-left disabled:opacity-60 ${
                          selected
                            ? "border-[#1DA8C6] bg-[#1DA8C6]/10"
                            : "border-white/10 bg-white/[0.02] hover:border-white/25"
                        }`}
                      >
                        <div className="mb-1 flex items-center gap-2">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/10 text-[10px] font-bold text-white">
                            {option.id}
                          </span>
                          <span className="text-xs font-medium text-slate-200">
                            Quality {option.qualityScore}
                          </span>
                          {selected && editedFromOption && (
                            <span className="rounded bg-amber-500/20 px-1.5 text-[9px] font-medium text-amber-200">
                              edited
                            </span>
                          )}
                          <span className="ml-auto font-mono text-[10px] text-slate-400">
                            {option.contactAreaMm2.toFixed(1)} mm²
                          </span>
                        </div>
                        <p className="font-mono text-[10px] text-slate-500">
                          gap {formatMm(option.finalMinGap)}
                          {!isPartialArch &&
                            ` · R/L ${option.rightContactAreaMm2.toFixed(0)}/${option.leftContactAreaMm2.toFixed(0)}`}
                          {` · ${option.sectorsWithContact}/${option.sectorsPresent} zones`}
                        </p>
                        <p className="mt-0.5 text-[10px] text-slate-600">
                          {option.id === 1
                            ? "Best seat"
                            : offsetParts.length > 0
                              ? `vs #1: ${offsetParts.join(", ")}`
                              : "Nearly the same as #1"}
                        </p>
                      </button>
                      {selected && editedFromOption && (
                        <button
                          type="button"
                          disabled={optimizingBite || recomputing}
                          onClick={() => applyBiteOption(option)}
                          className="mt-1 text-[10px] text-[#7dd3ea] hover:underline disabled:opacity-40"
                        >
                          Reset to option {option.id}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {stage === "done" && (
            <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-2.5">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Export optimized
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => exportOptimizedStl("upper")}
                  className="rounded-md border border-white/10 px-2 py-1.5 text-[10px] text-slate-300 hover:border-[#1DA8C6]/40 hover:text-white"
                >
                  Upper STL
                </button>
                <button
                  type="button"
                  onClick={() => exportOptimizedStl("lower")}
                  className="rounded-md border border-white/10 px-2 py-1.5 text-[10px] text-slate-300 hover:border-[#1DA8C6]/40 hover:text-white"
                >
                  Lower STL
                </button>
                <button
                  type="button"
                  onClick={() => exportOptimizedStl("both")}
                  className="rounded-md border border-emerald-500/30 bg-emerald-950/30 px-2 py-1.5 text-[10px] text-emerald-200 hover:bg-emerald-900/40"
                >
                  Both STLs
                </button>
                <button
                  type="button"
                  onClick={exportAlignment}
                  className="rounded-md border border-white/10 px-2 py-1.5 text-[10px] text-slate-300 hover:border-white/20 hover:text-white"
                >
                  Alignment JSON
                </button>
              </div>
              <p className="mt-1.5 text-[10px] leading-snug text-slate-600">
                Full-resolution arches in the current optimized pose (binary STL).
              </p>
            </div>
          )}

          {(progress || recomputing) && (
            <p className="mt-2.5 flex items-start gap-2 text-[11px] text-slate-400">
              {stage === "done" && !recomputing ? (
                <FiCheckCircle className="mt-0.5 shrink-0 text-emerald-400" />
              ) : stage === "error" ? (
                <FiX className="mt-0.5 shrink-0 text-red-400" />
              ) : (
                <FiLoader className="mt-0.5 shrink-0 animate-spin text-[#1DA8C6]" />
              )}
              <span>{recomputing ? "Updating contacts…" : progress}</span>
            </p>
          )}
          {error && (
            <p className="mt-2 rounded-lg border border-red-500/30 bg-red-950/40 px-2 py-1.5 text-[11px] text-red-300">
              {error}
            </p>
          )}
          {warnings.map((w) => (
            <p
              key={w}
              className="mt-2 rounded-lg border border-amber-500/30 bg-amber-950/30 px-2 py-1.5 text-[11px] text-amber-200"
            >
              {w}
            </p>
          ))}

          {stage === "done" && originalSnapshot && (
            <>
              <SectionLabel className="mt-5">Before / after</SectionLabel>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {(
                  [
                    ["optimized", "Optimized"],
                    ["original", "Original"],
                    ["compare", "Side by side"],
                  ] as const
                ).map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setBiteViewMode(mode)}
                    className={`rounded-md px-2 py-1 text-[10px] font-medium ${
                      biteViewMode === mode
                        ? "bg-[#1DA8C6] text-white"
                        : "border border-white/10 text-slate-400 hover:text-white"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {biteDelta && (
                <dl className="mb-2 grid grid-cols-2 gap-x-2 gap-y-1 rounded-lg border border-white/10 bg-white/[0.02] p-2 text-[11px]">
                  <dt className="text-slate-500">Min gap</dt>
                  <dd className="text-right font-mono text-slate-300">
                    {formatMm(biteDelta.minGapBefore)} → {formatMm(biteDelta.minGapAfter)}
                  </dd>
                  <dt className="text-slate-500">Improvement</dt>
                  <dd
                    className={`text-right font-mono ${
                      biteDelta.minGapImprovement > 0 ? "text-emerald-400" : "text-slate-400"
                    }`}
                  >
                    {biteDelta.minGapImprovement > 0 ? "−" : ""}
                    {formatMm(Math.abs(biteDelta.minGapImprovement))}
                  </dd>
                  <dt className="text-slate-500">Contact area</dt>
                  <dd className="text-right font-mono text-slate-300">
                    {biteDelta.areaBefore.toFixed(1)} → {biteDelta.areaAfter.toFixed(1)} mm²
                  </dd>
                  <dt className="text-slate-500">Max overlap</dt>
                  <dd className="text-right font-mono text-slate-300">
                    {formatMm(biteDelta.penetrationBefore)} → {formatMm(biteDelta.penetrationAfter)}
                  </dd>
                  <dt className="text-slate-500">Interference pts</dt>
                  <dd className="text-right text-slate-300">
                    {biteDelta.interferenceBefore.toLocaleString()} →{" "}
                    {biteDelta.interferenceAfter.toLocaleString()}
                  </dd>
                  {qualityScore != null && (
                    <>
                      <dt className="text-slate-500">Quality</dt>
                      <dd
                        className={`text-right font-mono font-semibold ${
                          qualityTone === "emerald"
                            ? "text-emerald-400"
                            : qualityTone === "amber"
                              ? "text-amber-300"
                              : "text-rose-300"
                        }`}
                      >
                        {qualityScore}/100
                      </dd>
                    </>
                  )}
                </dl>
              )}
              <p className="text-[10px] text-slate-600">
                Hold <span className="text-slate-400">Space</span> to peek original ·{" "}
                <span className="text-slate-400">Ctrl+Z</span> undo
              </p>
            </>
          )}

          {biteRegistration && !biteRegistration.skipped && (
            <>
              <SectionLabel className="mt-5">Bite registration</SectionLabel>
              <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
                {(
                  [
                    ["Upper", biteRegistration.upperRms, biteRegistration.upperOverlap],
                    ["Lower", biteRegistration.lowerRms, biteRegistration.lowerOverlap],
                  ] as const
                ).map(([label, rms, overlap]) => (
                  <Fragment key={label}>
                    <dt className="text-slate-500">{label} fit / overlap</dt>
                    <dd
                      className={`text-right font-mono ${
                        overlap < LOW_OVERLAP || rms > 0.1 ? "text-amber-300" : "text-slate-300"
                      }`}
                    >
                      {formatMm(rms)} · {(overlap * 100).toFixed(0)}%
                    </dd>
                  </Fragment>
                ))}
              </dl>
              <p className="mt-1 text-[10px] text-slate-600">
                Fit = RMS of surface on the bite record; overlap = share of the arch it covers.
              </p>
            </>
          )}

          <SectionLabel className="mt-5">Contact heat map</SectionLabel>
          <p className="mb-2 text-[10px] text-slate-600">
            Both jaws coloured by gap to the opposing arch — red through blue.
          </p>
          <div
            className="mb-3 h-2.5 w-full rounded-full"
            style={{
              background:
                "linear-gradient(90deg, #b80d1a 0%, #f97316 22%, #eab308 48%, #22c55e 62%, #06b6d4 78%, #3b82f6 100%)",
            }}
          />
          <ul className="space-y-1">
            {legend.map((item) => (
              <li key={item.label} className="flex items-center gap-2 text-[11px] text-slate-500">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: item.color }} />
                {item.label}
              </li>
            ))}
          </ul>

          {stage === "done" && (
            <>
              <SectionLabel className="mt-5">Peak exclusions (optional)</SectionLabel>
              <label className="flex cursor-pointer items-start gap-2 text-[11px] text-slate-500">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={excludePeaksEnabled}
                  onChange={(e) => setExcludePeaksEnabled(e.target.checked)}
                />
                Exclude painted areas from adjustment guidance
              </label>
              {excludePeaksEnabled && (
                <div className="mt-2 space-y-2 rounded-lg border border-white/10 bg-white/[0.02] p-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={toggleExcludePaint}
                      className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] ${
                        excludePaintMode
                          ? "bg-purple-600 text-white"
                          : "border border-white/10 text-slate-400 hover:text-white"
                      }`}
                    >
                      {excludePaintMode ? "Painting…" : "Paint exclusions"}
                    </button>
                    <button
                      type="button"
                      onClick={clearExclusions}
                      disabled={exclusionRegions.length === 0}
                      className="flex items-center gap-1 rounded-md border border-white/10 px-2 py-1.5 text-[11px] text-slate-400 hover:text-white disabled:opacity-40"
                    >
                      <FiTrash2 size={12} />
                      Clear
                    </button>
                  </div>
                  <label className="flex items-center justify-between text-[10px] text-slate-500">
                    Brush size (mm)
                    <select
                      className="rounded border border-white/10 bg-[#070b14] px-1 py-0.5 text-slate-300"
                      value={excludeBrushMm}
                      onChange={(e) => setExcludeBrushMm(Number(e.target.value))}
                    >
                      {BRUSH_SIZE_OPTIONS_MM.map((mm) => (
                        <option key={mm} value={mm}>
                          {mm}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="text-[10px] text-slate-600">
                    {exclusionRegions.length} excluded region
                    {exclusionRegions.length === 1 ? "" : "s"} · click/drag on upper arch
                  </p>
                </div>
              )}
            </>
          )}

          {contactAdjustments.length > 0 && (
            <>
              <div className="mt-5 mb-2 flex items-center justify-between">
                <SectionLabel className="!mt-0 !mb-0">
                  {MAX_CONTACT_ADJUSTMENTS} ways to max contact
                </SectionLabel>
                <button type="button" onClick={showOverview} className="text-[11px] text-[#1DA8C6] hover:underline">
                  Overview
                </button>
              </div>
              <p className="mb-2 text-[10px] text-slate-600">
                Move {guidanceJaw} jaw toward bite closure · ranked by gap improvement
              </p>
              <ul className="max-h-72 space-y-1.5 overflow-y-auto">
                {contactAdjustments.map((adj) => {
                  const active = selectedAdjustmentId === adj.id;
                  const colors = ["#22d3ee", "#34d399", "#fbbf24", "#fb923c", "#f472b6"];
                  return (
                    <li key={adj.id}>
                      <div
                        className={`w-full rounded-lg border px-2.5 py-2 text-left transition ${
                          active
                            ? "border-[#1DA8C6]/70 bg-[#1DA8C6]/10"
                            : "border-white/10 bg-white/[0.02] hover:border-white/20"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => selectAdjustment(adj)}
                          className="w-full text-left"
                        >
                          <div className="mb-1 flex items-center gap-2">
                            <span
                              className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
                              style={{ background: colors[(adj.id - 1) % colors.length] }}
                            >
                              {adj.id}
                            </span>
                            <span className="text-xs font-medium text-slate-200">{adj.label}</span>
                            <span className="ml-auto text-[10px] font-medium text-emerald-400">
                              mean gap −{adj.improvementMm.toFixed(3)} mm
                            </span>
                          </div>
                          <p className="mb-1 font-mono text-[11px] text-slate-400">
                            Step {adj.step}
                            {adj.axis.startsWith("r") ? "°" : " mm"} → gap{" "}
                            {formatMm(adj.projectedMinGap)}
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => applyAdjustment(adj)}
                          className="mt-1 rounded-md bg-[#1DA8C6]/20 px-2 py-0.5 text-[10px] font-medium text-[#7dd3ea] hover:bg-[#1DA8C6]/35"
                        >
                          Apply step
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {stats && (
            <>
              <SectionLabel className="mt-5">Summary</SectionLabel>
              {qualityScore != null && (
                <div
                  className={`mb-2 flex items-center justify-between rounded-lg border px-2.5 py-2 ${
                    qualityTone === "emerald"
                      ? "border-emerald-500/25 bg-emerald-950/30"
                      : qualityTone === "amber"
                        ? "border-amber-500/25 bg-amber-950/30"
                        : "border-rose-500/25 bg-rose-950/30"
                  }`}
                >
                  <span className="text-[11px] text-slate-400">Bite quality</span>
                  <span className="font-mono text-sm font-semibold text-white">
                    {qualityScore}
                    <span className="text-[11px] font-normal text-slate-500"> / 100</span>
                  </span>
                </div>
              )}
              <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
                <dt className="text-slate-500">Interfere</dt>
                <dd className="text-right text-red-400">{stats.interferenceCount.toLocaleString()}</dd>
                <dt className="text-slate-500">Max / light</dt>
                <dd className="text-right text-slate-300">
                  {stats.contactCount.toLocaleString()} / {stats.lightContactCount.toLocaleString()}
                </dd>
                <dt className="text-slate-500">Near / clear</dt>
                <dd className="text-right text-slate-300">
                  {stats.nearContactCount.toLocaleString()} / {stats.clearanceCount.toLocaleString()}
                </dd>
                <dt className="text-slate-500">Min gap</dt>
                <dd className="text-right font-mono text-slate-300">{formatMm(stats.minSignedDistance)}</dd>
                <dt className="text-slate-500">Mean gap</dt>
                <dd className="text-right font-mono text-slate-300">{formatMm(stats.meanSignedDistance)}</dd>
                <dt className="text-slate-500">Contact area</dt>
                <dd className="text-right font-mono text-slate-300">{stats.contactAreaMm2.toFixed(1)} mm²</dd>
                <dt className="text-slate-500">Max overlap</dt>
                <dd
                  className={`text-right font-mono ${
                    stats.maxPenetrationMm > PENETRATION_TOLERANCE_MM ? "text-red-400" : "text-slate-300"
                  }`}
                >
                  {formatMm(stats.maxPenetrationMm)}
                </dd>
                {!isPartialArch && (
                  <>
                    <dt className="text-slate-500">Right / left</dt>
                    <dd className="text-right font-mono text-slate-300">
                      {stats.rightContactAreaMm2.toFixed(1)} / {stats.leftContactAreaMm2.toFixed(1)} mm²
                    </dd>
                  </>
                )}
                <dt className="text-slate-500">Sectors in contact</dt>
                <dd className="text-right text-slate-300">
                  {stats.sectorsWithContact} / {stats.sectorsPresent}
                </dd>
              </dl>
              <ul className="mt-2 space-y-0.5">
                {ARCH_SECTORS.map((label, i) => {
                  const area = stats.sectorContactAreaMm2[i] ?? 0;
                  const max = Math.max(1, ...stats.sectorContactAreaMm2);
                  return (
                    <li key={label} className="flex items-center gap-2 text-[10px] text-slate-500">
                      <span className="w-28 shrink-0 truncate">{label}</span>
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/5">
                        <span
                          className="block h-full rounded-full bg-[#f97316]"
                          style={{ width: `${(area / max) * 100}%` }}
                        />
                      </span>
                      <span className="w-12 text-right font-mono">{area.toFixed(1)}</span>
                    </li>
                  );
                })}
              </ul>
              <dl className="mt-2 grid grid-cols-4 gap-1 text-center text-[10px]">
                {(
                  [
                    ["Overlap", stats.quality.penetration],
                    ["Coverage", stats.quality.coverage],
                    ["Balance", stats.quality.balance],
                    ["Amount", stats.quality.amount],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label} className="rounded-md border border-white/10 bg-white/[0.02] px-1 py-1">
                    <dt className="text-slate-500">{label}</dt>
                    <dd className="font-mono text-slate-300">{Math.round(value * 100)}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </aside>

        {panelOpen && (
          <button
            type="button"
            aria-label="Close panel"
            className="absolute inset-0 z-10 bg-black/50 md:hidden"
            onClick={() => setPanelOpen(false)}
          />
        )}

        {/* Viewport */}
        <main className="relative min-h-0 flex-1 bg-[#070b14]">
          {!meshes.upper && stage === "idle" && (
            <div className="absolute inset-0 z-[1] flex items-center justify-center p-6 text-center">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-8 py-10">
                <FiUpload className="mx-auto mb-3 text-3xl text-slate-600" />
                <p className="text-sm font-medium text-slate-200">Upload jaw & bite scans</p>
                <p className="mt-1 text-xs text-slate-500">
                  Full arch or partial segment (e.g. left last 4 teeth) · then run analysis
                </p>
              </div>
            </div>
          )}

          {loadingOverlay && (
            <ViewportLoadingOverlay
              title={loadingOverlay.title}
              detail={loadingOverlay.detail}
              percent={loadingOverlay.percent}
              onCancel={optimizingBite ? cancelAutoFind : undefined}
            />
          )}

          {recomputing && !loadingOverlay && stage === "done" && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 z-[16] -translate-x-1/2">
              <div className="flex items-center gap-1.5 rounded-full border border-[#1DA8C6]/40 bg-[#0b1220]/90 px-3 py-1 text-[11px] font-medium text-[#7dd3ea] shadow-lg">
                <FiLoader className="animate-spin" size={12} />
                Updating contacts…
              </div>
            </div>
          )}

          {peekOriginal && stage === "done" && originalSnapshot && (
            <div className="pointer-events-none absolute left-1/2 top-3 z-[16] -translate-x-1/2">
              <div className="rounded-full border border-amber-400/40 bg-amber-950/85 px-3 py-1 text-[11px] font-medium text-amber-100 shadow-lg">
                Peeking original bite — release Space
              </div>
            </div>
          )}

          {showInteractiveChrome && stage === "done" && contactAdjustments.length > 0 && (
            <div className="pointer-events-none absolute right-3 top-3 z-[2] w-[min(100%,17rem)]">
              <div className="pointer-events-auto rounded-xl border border-white/10 bg-[#0b1220]/92 p-3 shadow-2xl backdrop-blur-md">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Adjustment guidance
                  </p>
                  {recomputing && (
                    <span className="flex items-center gap-1 text-[10px] text-[#1DA8C6]">
                      <FiLoader className="animate-spin" /> updating
                    </span>
                  )}
                </div>
                <p className="mb-2 text-[11px] text-slate-400">
                  Both jaws heat-mapped · min gap{" "}
                  <span className="font-mono text-slate-200">
                    {stats ? formatMm(stats.minSignedDistance) : "—"}
                  </span>
                  {customActive && (
                    <span className="ml-1 rounded bg-amber-500/20 px-1 text-amber-300">tuned</span>
                  )}
                </p>
                <div className="mb-2 flex flex-wrap gap-1">
                  {contactAdjustments.map((adj) => {
                    const colors = ["#22d3ee", "#34d399", "#fbbf24", "#fb923c", "#f472b6"];
                    return (
                      <button
                        key={adj.id}
                        type="button"
                        onClick={() => selectAdjustment(adj)}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold text-white ${
                          selectedAdjustmentId === adj.id ? "ring-2 ring-white/80" : "opacity-90"
                        }`}
                        style={{ background: colors[(adj.id - 1) % colors.length] }}
                      >
                        #{adj.id}
                      </button>
                    );
                  })}
                </div>
                {selectedAdjustment && (
                  <div className="border-t border-white/10 pt-2 text-[11px] text-slate-300">
                    <p className="font-medium text-white">{selectedAdjustment.label}</p>
                    <p className="mt-0.5 font-mono text-slate-400">
                      Reduces mean contact-zone gap by ~{selectedAdjustment.improvementMm.toFixed(3)} mm
                    </p>
                    <button
                      type="button"
                      onClick={() => applyAdjustment(selectedAdjustment)}
                      className="mt-2 rounded-md bg-[#1DA8C6] px-2.5 py-1 text-[10px] font-medium text-white hover:bg-[#1897ad]"
                    >
                      Apply step
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {effectiveViewMode === "compare" && originalViewerMeshes ? (
            <div className="absolute inset-0 flex min-h-0">
              <div className="relative min-w-0 flex-1 border-r border-white/10">
                <OcclusionViewer
                  meshes={originalViewerMeshes}
                  upperTransform={IDENTITY_JAW}
                  lowerTransform={IDENTITY_JAW}
                  contactAdjustments={[]}
                  className="absolute inset-0 h-full w-full"
                  caption="Original bite (registered)"
                  captionClassName="pointer-events-none absolute left-3 top-3 z-10 rounded-lg border border-amber-500/30 bg-amber-950/85 px-2.5 py-1 text-[11px] font-semibold text-amber-100 shadow-lg backdrop-blur-sm"
                />
                {originalSnapshot && (
                  <div className="pointer-events-none absolute bottom-3 left-3 z-10 rounded-md bg-black/50 px-2 py-1 text-[10px] font-mono text-slate-300">
                    Min gap {formatMm(originalSnapshot.stats.minSignedDistance)}
                  </div>
                )}
              </div>
              <div className="relative min-w-0 flex-1">
                <OcclusionViewer
                  meshes={meshes}
                  upperTransform={IDENTITY_JAW}
                  lowerTransform={IDENTITY_JAW}
                  contactAdjustments={[]}
                  className="absolute inset-0 h-full w-full"
                  caption="Optimized bite"
                  captionClassName="pointer-events-none absolute left-3 top-3 z-10 rounded-lg border border-emerald-500/30 bg-emerald-950/85 px-2.5 py-1 text-[11px] font-semibold text-emerald-100 shadow-lg backdrop-blur-sm"
                />
                {stats && (
                  <div className="pointer-events-none absolute bottom-3 left-3 z-10 rounded-md bg-black/50 px-2 py-1 text-[10px] font-mono text-slate-300">
                    Min gap {formatMm(stats.minSignedDistance)}
                    {qualityScore != null ? ` · Q ${qualityScore}` : ""}
                  </div>
                )}
              </div>
            </div>
          ) : effectiveViewMode === "original" && originalViewerMeshes ? (
            <OcclusionViewer
              meshes={originalViewerMeshes}
              upperTransform={IDENTITY_JAW}
              lowerTransform={IDENTITY_JAW}
              contactAdjustments={[]}
              className="absolute inset-0 h-full w-full"
              caption="Original bite (before optimization)"
            />
          ) : (
            <OcclusionViewer
              ref={viewerRef}
              meshes={meshes}
              upperTransform={displayUpperTx}
              lowerTransform={displayLowerTx}
              jawDrag={showInteractiveChrome ? jawDragConfig : null}
              onJawDrag={handleJawDrag}
              onJawDragEnd={handleJawDragEnd}
              contactAdjustments={contactAdjustments}
              selectedAdjustmentId={selectedAdjustmentId}
              showAllAdjustments={showAllAdjustments}
              movableJaw={guidanceJaw}
              excludeMode={excludePaintMode}
              exclusionRegions={exclusionRegions}
              onAddExclusion={addExclusionRegion}
              priorityPaintMode={priorityPaintMode}
              priorityRegions={priorityRegions}
              onAddPriority={addPriorityRegion}
              className="absolute inset-0 h-full w-full"
              caption={
                customActive ? "Optimized bite (manual tuning)" : undefined
              }
            />
          )}

          {showInteractiveChrome && priorityPaintMode && (
            <div className="pointer-events-none absolute left-1/2 top-3 z-[2] -translate-x-1/2">
              <div className="rounded-full border border-amber-400/40 bg-amber-950/80 px-3 py-1 text-[11px] text-amber-100">
                Priority paint — mark critical bite contacts on upper arch (right-drag to pan)
              </div>
            </div>
          )}

          {showInteractiveChrome && excludePaintMode && (
            <div className="pointer-events-none absolute left-1/2 top-3 z-[2] -translate-x-1/2">
              <div className="rounded-full border border-purple-400/40 bg-purple-950/80 px-3 py-1 text-[11px] text-purple-200">
                Exclusion paint mode — click/drag upper arch (right-drag to pan view)
              </div>
            </div>
          )}

          {/* Custom alignment dock — draggable & hideable */}
          {showInteractiveChrome && stage === "done" && alignmentDockVisible && (
            <DraggableAlignmentPanel
              position={alignmentDockPos}
              onPositionChange={setAlignmentDockPos}
              onHide={() => setAlignmentDockVisible(false)}
            >
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <div className="flex rounded-lg border border-white/10 p-0.5">
                    {(["upper", "lower"] as const).map((j) => {
                      const locked = lockedJaw === j;
                      const active = displayJaw === j;
                      return (
                        <button
                          key={j}
                          type="button"
                          disabled={locked}
                          onClick={() => {
                            if (lockedJaw === "none") setActiveJaw(j);
                          }}
                          className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] capitalize ${
                            locked
                              ? "cursor-not-allowed bg-amber-500/20 text-amber-200"
                              : active
                                ? "bg-[#1DA8C6] text-white"
                                : "text-slate-400 hover:text-white"
                          }`}
                        >
                          {locked && <FiLock size={10} />}
                          {j}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex rounded-lg border border-white/10 p-0.5">
                    <button
                      type="button"
                      onClick={() => setJawLock(lockedJaw === "upper" ? "none" : "upper")}
                      className={`rounded-md px-2 py-1 text-[10px] ${
                        lockedJaw === "upper"
                          ? "bg-amber-500/25 text-amber-200"
                          : "text-slate-400 hover:text-white"
                      }`}
                      title="Fix upper jaw — move lower only"
                    >
                      Fix upper
                    </button>
                    <button
                      type="button"
                      onClick={() => setJawLock(lockedJaw === "lower" ? "none" : "lower")}
                      className={`rounded-md px-2 py-1 text-[10px] ${
                        lockedJaw === "lower"
                          ? "bg-amber-500/25 text-amber-200"
                          : "text-slate-400 hover:text-white"
                      }`}
                      title="Fix lower jaw — move upper only"
                    >
                      Fix lower
                    </button>
                  </div>
                  <div className="ml-auto flex items-center gap-2 text-[10px] text-slate-500">
                    <label className="flex items-center gap-1">
                      Telescopic
                      <select
                        className="rounded border border-white/10 bg-[#070b14] px-1 py-0.5 text-slate-300"
                        value={nudgeStep}
                        onChange={(e) => setNudgeStep(Number(e.target.value))}
                      >
                        <option value={0.01}>0.01 mm</option>
                        <option value={0.02}>0.02 mm</option>
                        <option value={0.05}>0.05 mm</option>
                        <option value={0.1}>0.1 mm</option>
                        <option value={0.25}>0.25 mm</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-1">
                      Tilt
                      <select
                        className="rounded border border-white/10 bg-[#070b14] px-1 py-0.5 text-slate-300"
                        value={rotStep}
                        onChange={(e) => setRotStep(Number(e.target.value))}
                      >
                        <option value={0.05}>0.05°</option>
                        <option value={0.1}>0.1°</option>
                        <option value={0.25}>0.25°</option>
                        <option value={0.5}>0.5°</option>
                        <option value={1}>1°</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={resetTransforms}
                      className="flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-slate-400 hover:bg-white/5 hover:text-white"
                      title="Reset custom alignment"
                    >
                      <FiRefreshCw size={12} />
                      Reset
                    </button>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-cyan-400/80">
                      Telescopic (mm)
                    </p>
                    <div className="space-y-1.5">
                      {TELESCOPIC_AXES.map(({ key, label }) => (
                        <AxisControl
                          key={key}
                          label={label}
                          value={activeTx[key]}
                          step={nudgeStep}
                          min={precisionMode ? -1.5 : -5}
                          max={precisionMode ? 1.5 : 5}
                          decimals={precisionMode ? 4 : 3}
                          onNudge={(dir) => nudge(key, dir)}
                          onChange={(v) => patchActive({ [key]: v })}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400/80">
                      Tilt (degrees)
                    </p>
                    <div className="space-y-1.5">
                      {TILT_AXES.map(({ key, label }) => (
                        <AxisControl
                          key={key}
                          label={label}
                          value={activeTx[key]}
                          step={rotStep}
                          min={precisionMode ? -2 : -15}
                          max={precisionMode ? 2 : 15}
                          decimals={3}
                          onNudge={(dir) => nudge(key, dir)}
                          onChange={(v) => patchActive({ [key]: v })}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="mt-2 text-center text-[10px] text-slate-600">
                  {lockedJaw === "none"
                    ? `Move ${displayJaw} to test occlusion — fix a jaw for precision telescopic + tilt`
                    : lockedJaw === "upper"
                      ? "Upper fixed — drag lower: move · Shift+drag: tilt · Alt+drag: yaw · Alt+wheel on jaw: close Z · wheel: zoom"
                      : "Lower fixed — drag upper: move · Shift+drag: tilt · Alt+drag: yaw · Alt+wheel on jaw: close Z · wheel: zoom"}
                </p>
            </DraggableAlignmentPanel>
          )}

          {showInteractiveChrome && stage === "done" && !alignmentDockVisible && (
            <button
              type="button"
              onClick={() => setAlignmentDockVisible(true)}
              className="absolute bottom-3 left-3 z-[2] flex items-center gap-2 rounded-full border border-[#1DA8C6]/40 bg-[#0b1220]/95 px-3 py-2 text-[11px] font-medium text-[#7dd3ea] shadow-lg backdrop-blur-md hover:bg-[#1DA8C6]/15"
              title="Show alignment panel"
            >
              <FiMove />
              Show alignment
            </button>
          )}
        </main>
      </div>
    </div>
  );
};

function DraggableAlignmentPanel({
  children,
  position,
  onPositionChange,
  onHide,
}: {
  children: ReactNode;
  position: { x: number; y: number } | null;
  onPositionChange: (pos: { x: number; y: number }) => void;
  onHide: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const panel = panelRef.current;
    const parent = panel?.parentElement;
    if (!panel || !parent || position !== null) return;

    const place = () => {
      const pW = parent.clientWidth;
      const pH = parent.clientHeight;
      const w = panel.offsetWidth;
      const h = panel.offsetHeight;
      onPositionChange({
        x: Math.max(8, (pW - w) / 2),
        y: Math.max(8, pH - h - 12),
      });
    };

    place();
    const ro = new ResizeObserver(place);
    ro.observe(panel);
    return () => ro.disconnect();
  }, [position, onPositionChange]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragRef.current || !panelRef.current?.parentElement) return;
      const parent = panelRef.current.parentElement;
      const panelW = panelRef.current.offsetWidth;
      const panelH = panelRef.current.offsetHeight;
      const maxX = Math.max(0, parent.clientWidth - panelW);
      const maxY = Math.max(0, parent.clientHeight - panelH);
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      onPositionChange({
        x: Math.min(maxX, Math.max(0, dragRef.current.originX + dx)),
        y: Math.min(maxY, Math.max(0, dragRef.current.originY + dy)),
      });
    };

    const onUp = () => {
      dragRef.current = null;
      setDragging(false);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [onPositionChange]);

  const onDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button")) return;
    if (!panelRef.current?.parentElement) return;
    e.preventDefault();
    const current = position ?? { x: 0, y: 0 };
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: current.x,
      originY: current.y,
    };
    setDragging(true);
  };

  return (
    <div
      ref={panelRef}
      className="absolute z-[3] w-[min(98%,52rem)]"
      style={
        position
          ? { left: position.x, top: position.y }
          : { bottom: 12, left: "50%", transform: "translateX(-50%)" }
      }
    >
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0b1220]/95 shadow-2xl backdrop-blur-md">
        <div
          role="toolbar"
          aria-label="Drag alignment panel"
          onPointerDown={onDragStart}
          className={`flex items-center gap-2 border-b border-white/10 bg-white/[0.03] px-3 py-2 ${
            dragging ? "cursor-grabbing" : "cursor-grab"
          }`}
        >
          <FiMove className="shrink-0 text-[#1DA8C6]" size={14} />
          <span className="min-w-0 flex-1 truncate text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Drag to reposition · telescopic & tilt
          </span>
          <button
            type="button"
            onClick={onHide}
            className="shrink-0 rounded-md border border-white/10 p-1 text-slate-400 hover:bg-white/5 hover:text-white"
            title="Hide alignment panel"
          >
            <FiMinimize size={14} />
          </button>
        </div>
        <div className="p-3">{children}</div>
      </div>
    </div>
  );
}

function ViewportLoadingOverlay({
  title,
  detail,
  percent,
  onCancel,
}: {
  title: string;
  detail: string;
  percent: number;
  onCancel?: () => void;
}) {
  return (
    <div
      className={`absolute inset-0 z-[15] flex items-center justify-center bg-[#070b14]/72 backdrop-blur-[2px] ${
        onCancel ? "pointer-events-auto" : "pointer-events-none"
      }`}
    >
      <div className="w-[min(92%,22rem)] rounded-2xl border border-white/10 bg-[#0b1220]/95 p-5 shadow-2xl">
        <div className="mb-3 flex items-center gap-2.5">
          <FiLoader className="shrink-0 animate-spin text-[#1DA8C6]" size={18} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white">{title}</p>
            <p className="truncate text-[11px] text-slate-400">{detail}</p>
          </div>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#1DA8C6] to-emerald-400 transition-[width] duration-300"
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="font-mono text-[10px] text-slate-500">{percent}%</span>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-rose-500/40 px-2.5 py-1 text-[11px] font-medium text-rose-200 hover:bg-rose-950/50"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function yieldToUi() {
  return new Promise<void>((resolve) => setTimeout(resolve, 16));
}

function HdrBtn({
  children,
  onClick,
  title,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className="flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1.5 text-xs text-slate-400 transition hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
    >
      {children}
    </button>
  );
}

function AxisControl({
  label,
  value,
  step,
  min,
  max,
  decimals,
  onNudge,
  onChange,
}: {
  label: string;
  value: number;
  step: number;
  min: number;
  max: number;
  decimals: number;
  onNudge: (dir: 1 | -1) => void;
  onChange: (value: number) => void;
}) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-1.5 py-1.5">
      <div className="mb-1 flex items-center justify-between px-0.5">
        <span className="text-[10px] font-medium text-slate-500">{label}</span>
        <span className="font-mono text-[10px] text-slate-300">{value.toFixed(decimals)}</span>
      </div>
      <div className="flex gap-1">
        <NudgeBtn onClick={() => onNudge(-1)}>−</NudgeBtn>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(Number(e.target.value).toFixed(decimals)))}
          className="min-w-0 flex-1 accent-[#1DA8C6]"
        />
        <NudgeBtn onClick={() => onNudge(1)}>+</NudgeBtn>
      </div>
    </div>
  );
}

function NudgeBtn({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-6 w-6 shrink-0 rounded border border-white/10 text-xs text-slate-400 hover:bg-white/10 hover:text-white"
    >
      {children}
    </button>
  );
}

function SectionLabel({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={`mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 ${className}`}
    >
      {children}
    </p>
  );
}

function SlotUpload({
  label,
  slot,
  state,
  onFile,
  onClear,
  accent,
  disabled,
}: {
  label: string;
  slot: Slot;
  state: SlotState;
  onFile: (slot: Slot, e: ChangeEvent<HTMLInputElement>) => void;
  onClear: (slot: Slot) => void;
  accent: string;
  disabled?: boolean;
}) {
  const id = `occ-${slot}`;
  return (
    <div
      className={`mb-1.5 rounded-lg border border-white/10 bg-white/[0.02] p-2 ${
        disabled ? "opacity-40" : ""
      }`}
    >
      <div className="mb-1 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
        <span className="text-[11px] font-medium text-slate-300">{label}</span>
      </div>
      {state.file ? (
        <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
          <span className="truncate" title={state.name}>
            {state.name}
          </span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onClear(slot)}
            className="text-slate-500 hover:text-white"
          >
            <FiX />
          </button>
        </div>
      ) : (
        <label
          htmlFor={id}
          className={`flex cursor-pointer items-center justify-center gap-1 rounded-md border border-dashed border-white/15 py-1.5 text-[11px] text-slate-500 hover:border-[#1DA8C6] hover:text-[#1DA8C6] ${
            disabled ? "pointer-events-none" : ""
          }`}
        >
          <FiUpload size={12} />
          Choose
          <input
            id={id}
            type="file"
            accept=".stl,model/stl"
            className="hidden"
            disabled={disabled}
            onChange={(e) => onFile(slot, e)}
          />
        </label>
      )}
    </div>
  );
}

export default OcclusionContactAnalysis;
